// Pure maths for the Appearances widget (FE — #5 backlog). The widget is a thin
// SVG view over these rows; all the numeric work lives here so it's unit-tested
// and the geometry can be pre-verified against real data (the viz method that
// worked for the shot/pass maps + HOPS).
//
// Source: GET /player-appearances (one row per match). We turn each into a
// timeline row = the span on the pitch (on→off minute) on a single continuous
// match-minute axis, plus goal/yellow dots and how the player left the match.
//
// Minute model (from the golden pipeline, confirmed against real payloads):
//  - on/off/event minutes are already CONTINUOUS match minutes (a 63' in period
//    2 is the 63rd minute, not 63 into the half), so we only CAP stoppage time
//    to the period boundary — a 93' finish in period 2 shows as 90.
//  - period ≥ 3 = extra time; the axis extends to 105/120 only if some match
//    actually went there.
// Mirrors Catalyst's bio-tab timeline (soccer-frontend players/bio), minus the
// hide-extra-time toggle (we always show real minutes).

import type { PlayerAppearance } from "@catbento/shared";

// The last regular minute of each period. Used to clamp stoppage-time minutes so
// a bar/dot never runs past its period on the axis.
const PERIOD_CAPS: Record<number, number> = { 1: 45, 2: 90, 3: 105, 4: 120 };

/** How the player left the match — drives the exit marker's colour/label. */
export type ExitKind = "sub" | "red" | "injury" | "end" | "none";

/** In-match events we mark on the bar (Catalyst marks goals + yellows only). */
export type EventKind = "goal" | "yellow";

export interface TimelineEvent {
  kind: EventKind;
  /** Clamped continuous match minute (axis coordinate). */
  minute: number;
}

export interface TimelineRow {
  matchId: number;
  date: string;
  started: boolean;
  /** minutesOnPitch, rounded for display; 0 = in squad, no time played. */
  minutes: number;
  played: boolean;
  /** Clamped on/off minutes on the continuous axis (bar span). */
  on: number;
  off: number;
  exit: ExitKind;
  events: TimelineEvent[];
}

export interface Timeline {
  rows: TimelineRow[];
  /** Right edge of the minute axis: 90, or 105/120 if any match went to ET. */
  axisMax: number;
  apps: number;
  starts: number;
  totalMinutes: number;
}

/** Clamp a stoppage-time minute back to its period boundary (e.g. 93'→90). */
export function clampMinute(minute: number | null, period: number | null): number {
  if (minute === null) return 0;
  if (period === null) return minute;
  const cap = PERIOD_CAPS[period];
  return cap !== undefined && minute > cap ? cap : minute;
}

/** Map the golden off_pitch_type string to an exit kind. Unknown → "none". */
export function classifyExit(offPitchType: string | null): ExitKind {
  switch (offPitchType) {
    case "Substitution":
      return "sub";
    case "Red Card":
    case "Second Yellow Card":
      return "red"; // sent off — a hard end to the match
    case "Injury":
      return "injury";
    case "Match End":
      return "end";
    default:
      return "none";
  }
}

/** Pull goal + yellow-card events (with clamped minutes) out of the opaque
 *  appearance_metrics.key_events. Shots/own-goals are ignored (parity with
 *  Catalyst, which marks only goals + yellows). Defensive against bad shapes. */
export function extractEvents(appearanceMetrics: unknown): TimelineEvent[] {
  if (!appearanceMetrics || typeof appearanceMetrics !== "object") return [];
  const raw = (appearanceMetrics as Record<string, unknown>)["key_events"];
  if (!Array.isArray(raw)) return [];
  const out: TimelineEvent[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object") continue;
    const ev = e as Record<string, unknown>;
    const type = ev["type"];
    const minute = ev["minute"];
    const period = ev["period"];
    if (typeof minute !== "number") continue;
    const m = clampMinute(minute, typeof period === "number" ? period : null);
    if (type === "goal") out.push({ kind: "goal", minute: m });
    else if (type === "yellow_card") out.push({ kind: "yellow", minute: m });
  }
  return out;
}

/** Widest period reached across all appearances → the axis right edge. */
export function axisMaxFor(appearances: PlayerAppearance[]): number {
  let maxPeriod = 2; // regular time
  for (const a of appearances) {
    for (const p of [a.onPitchPeriod, a.offPitchPeriod]) {
      if (typeof p === "number" && p > maxPeriod) maxPeriod = p;
    }
  }
  return PERIOD_CAPS[maxPeriod] ?? 90;
}

export interface TimelineOptions {
  /** Cap stoppage-time minutes to the period boundary (93'→90) and end the axis
   *  at 90/105/120. Default true — the v1 appearances widget's original look.
   *  The v2 Matches widget passes **false** to show real stoppage time (a 94'
   *  goal at 94, the axis extended to fit), matching Ben's prototype. */
  clampToPeriod?: boolean;
}

/** A data-driven axis right edge: the largest minute we need to plot (bars +
 *  any dots), rounded UP to the next 10, with a 90 floor. Lets the real-minute
 *  Matches axis fit stoppage time (a 94' goal → axis 100) and extra time (118'
 *  → 120) without clipping, instead of a fixed cap. Empty input → 90. */
export function axisMaxForValues(minutes: number[]): number {
  let max = 90;
  for (const m of minutes) if (Number.isFinite(m) && m > max) max = m;
  return Math.ceil(max / 10) * 10;
}

/** Turn the raw appearances into timeline rows + the summary header numbers.
 *  Rows are newest-first (latest match at the top) to match old Catalyst — the
 *  API returns them oldest-first, so we sort by date descending here. */
export function buildTimeline(
  appearances: PlayerAppearance[],
  options: TimelineOptions = {}
): Timeline {
  const clamp = options.clampToPeriod ?? true;
  const rows: TimelineRow[] = appearances.map((a) => {
    const minutes = Math.round(a.minutesOnPitch ?? 0);
    const played = (a.minutesOnPitch ?? 0) > 0;
    // Real-minute mode keeps stoppage time (off can exceed 90); the axis is sized
    // to fit by the caller (v2 Matches). Nulls fall back to 0 as in the clamp path.
    const on = clamp ? clampMinute(a.onPitchMinute, a.onPitchPeriod) : a.onPitchMinute ?? 0;
    const off = clamp ? clampMinute(a.offPitchMinute, a.offPitchPeriod) : a.offPitchMinute ?? 0;
    return {
      matchId: a.matchId,
      date: a.matchDate,
      started: a.inStartingXi,
      minutes,
      played,
      on,
      off,
      exit: classifyExit(a.offPitchType),
      events: extractEvents(a.appearanceMetrics),
    };
  });

  // Newest match at the top (ISO date strings sort lexicographically = by date).
  rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return {
    rows,
    axisMax: axisMaxFor(appearances),
    apps: rows.length,
    starts: rows.filter((r) => r.started).length,
    totalMinutes: rows.reduce((sum, r) => sum + r.minutes, 0),
  };
}
